"use client";

import React, { useMemo, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAccount } from "@getpara/react-sdk";
import { SendModal } from "./send-modal";
import { NewCreditCardForm } from "@/components/forms/new-credit-card-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Send,
  ArrowUpRight,
  ArrowLeft,
  Repeat,
  RefreshCw,
  Eye,
  EyeOff,
  X,
  Copy,
  Check,
  CreditCard,
  Info,
} from "lucide-react";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { useTokenStore } from "@/lib/zustand";
import { usePortfolioStore } from "./mobile-portfolio-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTokenBalances, refreshEvmBalances } from "@/hooks/use-token-balances";
import { TokenBalance } from "@/constants/types";
import { ChainId, ChainKey, ChainType, getToken } from "@lifi/sdk";
import { getChainById } from "@/constants/get-chain";
import { useQueryClient } from "@tanstack/react-query";
import { zeroAddress } from "viem";
import { toast } from "sonner";
import { useBalanceSyncStore } from "@/lib/balance-sync-store";
import { refreshAllOverrides } from "@/lib/on-chain-balance-reader";
import { useAccount as useWagmiAccount } from "wagmi";
import { logDiagnostic } from "@/lib/silent-diagnostic";
import { useHiddenTokens } from "@/hooks/use-hidden-tokens";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const CHAIN_LOGOS: Record<number, string> = {
  1: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/ethereum.svg",
  56: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/bsc.svg",
  137: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/polygon.svg",
  42161: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/arbitrum.svg",
  10: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/optimism.svg",
  43114: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/avalanche.svg",
  8453:
    "https://raw.githubusercontent.com/base-org/brand-kit/001c0e9b40a67799ebe0418671ac4e02a0c683ce/logo/in-product/Base_Network_Logo.svg",
  1151111081099710: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
};

const NATIVE_TOKENS: Record<
  number,
  {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    logoURI: string;
  }
> = {
  1: {
    address: zeroAddress,
    symbol: "ETH",
    name: "Ethereum",
    decimals: 18,
    logoURI:
      "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  },
  56: {
    address: zeroAddress,
    symbol: "BNB",
    name: "BNB",
    decimals: 18,
    logoURI:
      "https://assets.coingecko.com/coins/images/825/small/binance-coin-logo.png",
  },
  137: {
    address: zeroAddress,
    symbol: "POL",
    name: "Polygon",
    decimals: 18,
    logoURI:
      "https://assets.coingecko.com/coins/images/4713/small/polygon.png",
  },
  42161: {
    address: zeroAddress,
    symbol: "ETH",
    name: "Ethereum",
    decimals: 18,
    logoURI:
      "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  },
  10: {
    address: zeroAddress,
    symbol: "ETH",
    name: "Ethereum",
    decimals: 18,
    logoURI:
      "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  },
  43114: {
    address: zeroAddress,
    symbol: "AVAX",
    name: "Avalanche",
    decimals: 18,
    logoURI:
      "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  },
  8453: {
    address: zeroAddress,
    symbol: "ETH",
    name: "Ethereum",
    decimals: 18,
    logoURI:
      "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  },
  1151111081099710: {
    address: "11111111111111111111111111111111",
    symbol: "SOL",
    name: "Solana",
    decimals: 9,
    logoURI: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  },
};

const USDC_TOKENS: Record<
  number,
  {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    logoURI: string;
  }
> = {
  1: {
    address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/logo.png",
  },
  56: {
    address: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 18,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/smartchain/assets/0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d/logo.png",
  },
  137: {
    address: "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/polygon/assets/0x3c499c542cef5e3811e1192ce70d8cc03d5c3359/logo.png",
  },
  42161: {
    address: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/arbitrum/assets/0xaf88d065e77c8cC2239327C5EDb3A432268e5831/logo.png",
  },
  10: {
    address: "0x0b2C639c533813f4Aa9D7837CaF62653d097fF85",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/optimism/assets/0x0b2C639c533813f4Aa9D7837CaF62653d097fF85/logo.png",
  },
  43114: {
    address: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/avalanchec/assets/0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E/logo.png",
  },
  8453: {
    address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/base/assets/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913/logo.png",
  },
  1151111081099710: {
    address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    logoURI:
      "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/logo.png",
  },
};

const getHumanReadableBalance = ({
  balance,
  decimals,
  balanceUSD,
}: Pick<TokenBalance, "balance" | "decimals" | "balanceUSD">) => {
  const numericBalance = Number(balance);

  if (!Number.isFinite(numericBalance) || numericBalance === 0 || decimals <= 0) {
    return numericBalance;
  }

  const balanceAsString = balance?.trim();
  if (!balanceAsString) {
    return numericBalance;
  }
  const alreadyFormatted = /[.eE]/.test(balanceAsString);
  if (alreadyFormatted) {
    return numericBalance;
  }

  const normalizedBalance = numericBalance / Math.pow(10, decimals);

  if (balanceUSD <= 0 || normalizedBalance <= 0) {
    return numericBalance;
  }

  const rawImpliedPrice = balanceUSD / numericBalance;
  const normalizedImpliedPrice = balanceUSD / normalizedBalance;
  const looksLikeRawUnits =
    numericBalance >= Math.pow(10, decimals) &&
    rawImpliedPrice < 1e-9 &&
    normalizedImpliedPrice > rawImpliedPrice * Math.pow(10, Math.min(decimals, 12)) * 0.1;

  return looksLikeRawUnits ? normalizedBalance : numericBalance;
};

export function PortfolioSidebar() {
  const { isOpen, setIsOpen } = usePortfolioStore();
  const [showBalances, setShowBalances] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sendModal, setSendModal] = useState<{
    isOpen: boolean;
    token: TokenBalance | null;
    network: string;
  }>({
    isOpen: false,
    token: null,
    network: "",
  });

  const { isConnected } = useAccount();
  const queryClient = useQueryClient();
  const { address } = useParaAccount();
  const { address: wagmiAddress } = useWagmiAccount();
  const [isCopied, setIsCopied] = useState(false);
  const [isBuyOpen, setIsBuyOpen] = useState(false);
  const { toToken, setFromToken, setFromChain, setToToken, setToChain } = useTokenStore();
  const [selectedTokenDetail, setSelectedTokenDetail] = useState<TokenBalance | null>(null);

  // Use the shared token balances hook for consistency with send modal
  const { availableTokens, isLoading } = useTokenBalances(refreshKey);

  // Hidden tokens management (per-wallet, persisted to localStorage)
  const solanaWalletAddress = address; // For Solana mode, address is the Solana address
  const { hiddenTokenIds, hideToken, unhideToken, isHidden, hiddenCount } = useHiddenTokens(solanaWalletAddress);
  const [showHiddenSection, setShowHiddenSection] = useState(false);

  // Post-swap sync state - shows placeholder while balance is syncing
  const isSyncing = useBalanceSyncStore((s) => s.isSyncing);

  // Rate limiting for refresh button: 3 presses per 10s, then 15s cooldown
  const REFRESH_WINDOW_MS = 10_000;
  const REFRESH_MAX_IN_WINDOW = 3;
  const REFRESH_COOLDOWN_MS = 15_000;
  const refreshTimestampsRef = useRef<number[]>([]);
  const cooldownUntilRef = useRef<number>(0);

  // Silent diagnostic: track last logged balance per symbol to avoid spamming
  const lastLoggedBalanceRef = useRef<Map<string, string>>(new Map());

  // Resolve EVM address (wagmi for external, Para for email wallets)
  const userEvmAddress = wagmiAddress || address;

  // Filter tokens with balance > 0 and hide dust below $0.05 from portfolio display
  // Exception: always show ETH if balance > 0 (even if balanceUSD is 0 due to price fetch timing)
  const allDisplayableTokens = availableTokens.filter(
    (token) => Number(token.balance) > 0 &&
      ((token.balanceUSD ?? 0) >= 0.05 || (parseFloat(token.balance) > 0 && token.symbol === 'ETH'))
  );

  // Separate visible and hidden tokens (only Solana SPL tokens can be hidden)
  const displayTokens = allDisplayableTokens.filter((token) => {
    // Native tokens cannot be hidden
    if (token.isNative) return true;
    // Only Solana tokens can be hidden for now
    if (token.network !== "Solana") return true;
    // Check if this token is in the hidden list
    return !isHidden(token.address);
  });

  const hiddenTokens = allDisplayableTokens.filter((token) => {
    if (token.isNative) return false;
    if (token.network !== "Solana") return false;
    return isHidden(token.address);
  });

  const normalizedDisplayTokens = useMemo(
    () =>
      displayTokens.map((token) => ({
        ...token,
        balance: getHumanReadableBalance(token).toString(),
      })),
    [displayTokens]
  );

  const normalizedHiddenTokens = useMemo(
    () =>
      hiddenTokens.map((token) => ({
        ...token,
        balance: getHumanReadableBalance(token).toString(),
      })),
    [hiddenTokens]
  );

  const normalizedSelectedTokenDetail = useMemo(
    () =>
      selectedTokenDetail
        ? {
            ...selectedTokenDetail,
            balance: getHumanReadableBalance(selectedTokenDetail).toString(),
          }
        : null,
    [selectedTokenDetail]
  );

  const totalPortfolioValue = normalizedDisplayTokens.reduce(
    (sum, token) => sum + (token.balanceUSD || 0),
    0
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatBalance = (balance: string, symbol: string) => {
    const num = parseFloat(balance);
    if (num === 0) return "0";
    if (num < 0.001) return `<0.001 ${symbol}`;
    if (num < 1) return `${num.toFixed(4)} ${symbol}`;
    if (num < 1000) return `${num.toFixed(3)} ${symbol}`;
    return `${num.toFixed(2)} ${symbol}`;
  };

  const formatAddress = (addr?: string) => {
    if (!addr) return "";
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const handleRefresh = async () => {
    if (!isConnected) return;

    const now = Date.now();

    // Check if in cooldown
    if (now < cooldownUntilRef.current) {
      const secondsLeft = Math.ceil((cooldownUntilRef.current - now) / 1000);
      toast.error(`Please wait ${secondsLeft}s before refreshing again`, {
        id: "refresh-cooldown",
        duration: 2000,
      });
      return;
    }

    // Hard-block while a refresh is in-flight
    if (isRefreshing) return;

    // Prune old timestamps outside the window
    refreshTimestampsRef.current = refreshTimestampsRef.current.filter(
      (ts) => now - ts < REFRESH_WINDOW_MS
    );

    // Check if user is spamming
    if (refreshTimestampsRef.current.length >= REFRESH_MAX_IN_WINDOW) {
      cooldownUntilRef.current = now + REFRESH_COOLDOWN_MS;
      refreshTimestampsRef.current = [];
      toast.error("Too many refreshes — please wait 15 seconds", {
        id: "refresh-cooldown",
        duration: 3000,
      });
      return;
    }

    // Record this press
    refreshTimestampsRef.current.push(now);

    // Proceed with refresh
    setIsRefreshing(true);

    // 10-second safety timeout in case something hangs
    const safetyTimer = setTimeout(() => setIsRefreshing(false), 10_000);

    try {
      setRefreshKey((current) => current + 1);

      // Refresh EVM balances (clears cache and triggers re-fetch)
      refreshEvmBalances();

      // Reset balance-related query keys
      queryClient.resetQueries({ queryKey: ["useProfileBalance"] });
      queryClient.resetQueries({ queryKey: ["balance"] });

      // Also invalidate these for good measure
      await queryClient.invalidateQueries({ queryKey: ["profileBalance"] });
      await queryClient.invalidateQueries({ queryKey: ["tokenBalances"] });

      // Re-read on-chain overrides (USD values computed at injection time from Para prices)
      if (userEvmAddress) {
        await refreshAllOverrides(userEvmAddress);
      }

      if (process.env.NODE_ENV !== "production") {
        console.log("🔬 [Manual refresh] completed");
      }
    } catch (e) {
      console.warn("Refresh failed:", e);
    } finally {
      clearTimeout(safetyTimer);
      setIsRefreshing(false);
    }
  };

  const copyAddress = (address: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(address);
    }
  };

  const getTokenContractLabel = (token: TokenBalance | null) => {
    if (!token?.address) return "Native Token";
    if (token.isNative) return "Native Token";
    if (token.address.length <= 12) return token.address;
    return `${token.address.slice(0, 4)}...${token.address.slice(-4)}`;
  };

  const mapTokenToChain = (token: TokenBalance) => {
    if (token.network === "Solana") {
      return {
        id: ChainId.SOL,
        name: "Solana",
        logoURI:
          "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
        chainType: ChainType.SVM,
        symbol: ChainKey.SOL,
      };
    }

    if (token.evmChainId) {
      const chain = getChainById(token.evmChainId);
      return {
        id: token.evmChainId,
        name: chain?.name || "Unknown",
        logoURI: CHAIN_LOGOS[token.evmChainId] || "",
        chainType: ChainType.EVM,
        symbol: (chain?.nativeCurrency?.symbol as ChainKey) || ChainKey.ETH,
      };
    }

    return {
      id: ChainId.ETH,
      name: token.network || "Ethereum",
      logoURI:
        "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/ethereum.svg",
      chainType: ChainType.EVM,
      symbol: ChainKey.ETH,
    };
  };

  const handleSwapFromPortfolio = async (token: TokenBalance) => {
    try {
      const chainId =
        token.network === "Solana" ? ChainId.SOL : (token.evmChainId || ChainId.ETH);
      const isSolanaNative = token.network === "Solana" && token.isNative;
      const isEvmNative = token.isNative && token.network !== "Solana";
      const fromChainObject = mapTokenToChain(token);

      if (token.network === "Solana") {
        // Try to get token from LiFi for accurate decimals
        let lifiToken = null;
        if (!isSolanaNative && token.address) {
          try {
            lifiToken = await getToken(ChainId.SOL, token.address);
          } catch {
            lifiToken = null;
          }
        }

        // Use LiFi decimals if available, then portfolio decimals, then 9 as last resort
        // Important: token.decimals from portfolio uses on-chain uiAmountString which has correct decimals
        const tokenDecimals = lifiToken?.decimals ?? token.decimals ?? 9;

        const fallbackToken = {
          address: isSolanaNative
            ? "11111111111111111111111111111111"
            : token.address || "11111111111111111111111111111111",
          symbol: token.symbol,
          name: token.name,
          decimals: tokenDecimals,
          chainId: ChainId.SOL,
          logoURI: token.logoURI || "",
          priceUSD: token.price?.toString() || "0",
        };

        if (process.env.NODE_ENV !== "production") {
          console.log("[Portfolio Swap] Setting fromToken", {
            symbol: token.symbol,
            portfolioDecimals: token.decimals,
            lifiDecimals: lifiToken?.decimals,
            finalDecimals: tokenDecimals,
            usingLifi: !!lifiToken,
          });
        }

        setFromToken(lifiToken || fallbackToken);
        setFromChain(fromChainObject);
      } else {
        setFromToken({
          address: isEvmNative ? zeroAddress : token.address || zeroAddress,
          symbol: token.symbol,
          name: token.name,
          decimals: token.decimals || 18,
          chainId,
          logoURI: token.logoURI || "",
          priceUSD: token.price?.toString() || "0",
        });

        if (token.evmChainId) {
          const chain = getChainById(token.evmChainId);
          if (chain) {
            setFromChain({
              id: token.evmChainId,
              name: chain.name,
              logoURI: CHAIN_LOGOS[token.evmChainId] || "",
              chainType: ChainType.EVM,
              symbol: (chain.nativeCurrency?.symbol as ChainKey) || ChainKey.ETH,
            });
          }
        } else {
          setFromChain(mapTokenToChain(token));
        }
      }

      const selectedChainId =
        token.network === "Solana" ? 1151111081099710 : (token.evmChainId || 1);
      const nativeToken = NATIVE_TOKENS[selectedChainId];
      const usdcToken = USDC_TOKENS[selectedChainId];

      const selectedIsNative = token.isNative ||
        (nativeToken &&
          token.symbol?.toUpperCase() === nativeToken.symbol.toUpperCase() &&
          (
            selectedChainId === 1151111081099710
              ? token.address === nativeToken.address
              : (token.address || zeroAddress).toLowerCase() ===
                nativeToken.address.toLowerCase()
          ));

      const destinationToken = selectedIsNative ? usdcToken : nativeToken;
      if (destinationToken) {
        setToToken({
          chainId: selectedChainId,
          address: destinationToken.address,
          symbol: destinationToken.symbol,
          name: destinationToken.name,
          decimals: destinationToken.decimals,
          logoURI: destinationToken.logoURI,
          priceUSD: "0",
        });
        setToChain(fromChainObject);
      }
    } catch (error) {
      console.error("Error setting swap token from portfolio:", error);
    }

    setSelectedTokenDetail(null);
    setIsOpen(false);
  };

  const closeSendModal = () => {
    setSendModal({ isOpen: false, token: null, network: "" });
  };

  const getNetworkBadgeStyle = (network: string) => {
    const styles: Record<string, string> = {
      Ethereum: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      Base: "bg-blue-400/20 text-blue-300 border-blue-400/30",
      Polygon: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      Arbitrum: "bg-blue-600/20 text-blue-400 border-blue-600/30",
      Optimism: "bg-[#f9cd4f]/20 text-[#f9cd4f] border-[#f9cd4f]/30",
      Solana: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      BNB: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      Bsc: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    };
    return styles[network] || "bg-muted/30 text-muted-foreground border-border";
  };

  const getNetworkBadgeText = (network: string) => {
    const texts: Record<string, string> = {
      Ethereum: "ETH",
      Polygon: "POL",
      Base: "BASE",
      Arbitrum: "ARB",
      Optimism: "OP",
      Solana: "SOL",
      BNB: "BNB",
      Bsc: "BNB",
    };
    return texts[network] || network?.substring(0, 3)?.toUpperCase() || "";
  };

  return (
    <>
      {/* Sidebar - only render content when connected, but always render modals below */}
      {isConnected ? (
        <>
          <div
            className={`fixed right-0 top-0 h-full w-80 bg-background border-l border-border transform transition-transform duration-300 ease-out z-[60] ${isOpen ? "translate-x-0" : "translate-x-full"
              }`}
          >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Wallet className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-foreground font-semibold text-lg">Portfolio</h2>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-[#f9cd4f] cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                      <p>Tokens received via airdrops or transfers can sometimes be scams. Use the hide button on any token row to remove unwanted tokens from your portfolio view.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefresh}
                  disabled={isLoading || isRefreshing}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoading || isRefreshing ? "animate-spin" : ""}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowBalances(!showBalances)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  {showBalances ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Connected Address */}
            {address && (
              <button
                onClick={() => {
                  copyAddress(address);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 1200);
                }}
                className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-input/50 hover:bg-input transition-colors w-full"
              >
                <span className="text-xs text-muted-foreground font-mono flex-1 text-left">
                  {formatAddress(address)}
                </span>
                {isCopied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </button>
            )}

            {/* Total Portfolio Value */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-muted-foreground text-sm mb-1">
                  Total Value
                </p>
                {isSyncing ? (
                  <p className="text-muted-foreground text-lg font-medium animate-pulse">
                    Syncing balance...
                  </p>
                ) : showBalances ? (
                  <p className="text-foreground text-3xl font-bold">
                    {formatCurrency(totalPortfolioValue)}
                  </p>
                ) : (
                  <p className="text-foreground text-3xl font-bold">••••••</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Token List */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-card rounded-xl"></div>
                  </div>
                ))}
              </div>
            ) : displayTokens.length === 0 ? (
              <div className="text-center py-12">
                <div className="p-4 rounded-full bg-muted/30 w-fit mx-auto mb-4">
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No tokens found</p>
              </div>
            ) : selectedTokenDetail ? (
              <Card className="bg-card border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTokenDetail(null)}
                    className="px-2 h-8 text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedTokenDetail(null);
                      setIsOpen(false);
                    }}
                    className="h-8 w-8 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-3 mb-5">
                  <Avatar className="h-12 w-12 rounded-xl">
                    {normalizedSelectedTokenDetail?.logoURI && (
                      <AvatarImage src={normalizedSelectedTokenDetail.logoURI} alt={normalizedSelectedTokenDetail?.symbol || "token"} className="rounded-xl object-cover" />
                    )}
                    <AvatarFallback className="rounded-xl bg-primary/20 text-primary font-semibold">
                      {normalizedSelectedTokenDetail?.symbol?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-foreground font-semibold text-base truncate">
                      {normalizedSelectedTokenDetail?.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {normalizedSelectedTokenDetail?.symbol}
                    </p>
                    <p className="text-sm text-foreground mt-1">
                      {showBalances
                        ? formatBalance(normalizedSelectedTokenDetail?.balance || "0", normalizedSelectedTokenDetail?.symbol || "")
                        : "••••••"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {showBalances ? formatCurrency(normalizedSelectedTokenDetail?.balanceUSD || 0) : "••••"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Button
                    onClick={() => normalizedSelectedTokenDetail && handleSwapFromPortfolio(normalizedSelectedTokenDetail)}
                    className="h-auto py-2.5 bg-[linear-gradient(135deg,#f9cd4f,#d4a012)] text-[#111] hover:brightness-105"
                  >
                    <Repeat className="h-4 w-4 mr-1.5" />
                    Swap
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSendModal({
                        isOpen: true,
                        token: normalizedSelectedTokenDetail,
                        network: normalizedSelectedTokenDetail?.network || "",
                      });
                      setSelectedTokenDetail(null);
                      setIsOpen(false);
                    }}
                    className="h-auto py-2.5 border-border text-foreground"
                  >
                    <Send className="h-4 w-4 mr-1.5" />
                    Transfer
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={normalizedSelectedTokenDetail?.isNative || !normalizedSelectedTokenDetail?.address}
                    onClick={() => {
                      if (normalizedSelectedTokenDetail?.address) {
                        copyAddress(normalizedSelectedTokenDetail.address);
                        toast.success("Copied!");
                      }
                    }}
                    className="h-auto py-2.5 text-muted-foreground hover:text-foreground disabled:opacity-80"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    {normalizedSelectedTokenDetail?.isNative || !normalizedSelectedTokenDetail?.address
                      ? "Native"
                      : "Copy"}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground px-1">
                  {normalizedSelectedTokenDetail?.isNative || !normalizedSelectedTokenDetail?.address
                    ? "Native Token"
                    : `Contract: ${getTokenContractLabel(normalizedSelectedTokenDetail)}`}
                </p>
              </Card>
            ) : (
              <div className={`space-y-2 transition-opacity ${isSyncing ? "opacity-50" : ""}`}>
                {normalizedDisplayTokens.map(
                  (token: TokenBalance, index: number) => {
                    // Silent diagnostic: throttled ui-render logging
                    const diagKey = `${token.symbol}-${token.network}`;
                    const lastBalance = lastLoggedBalanceRef.current.get(diagKey);
                    if (lastBalance !== token.balance) {
                      logDiagnostic({ event: "ui-render", symbol: token.symbol, balance: token.balance, balanceUSD: token.balanceUSD });
                      lastLoggedBalanceRef.current.set(diagKey, token.balance);
                    }

                    const isSolanaToken = token.network === "Solana";
                    const canHide = isSolanaToken && !token.isNative;

                    return (
                    <div
                      key={`${token.symbol}-${token.network}-${token.address || 'native'}-${index}`}
                      className="group relative"
                    >
                      <button
                        onClick={() => setSelectedTokenDetail(token)}
                        className="w-full p-3 rounded-xl bg-card border border-border hover:border-primary/30 hover:bg-card/80 transition-all flex items-start gap-3"
                      >
                        <Avatar className="h-10 w-10 rounded-xl">
                          {token.logoURI && (
                            <AvatarImage src={token.logoURI} alt={token.symbol} className="rounded-xl object-cover" />
                          )}
                          <AvatarFallback
                            className={`rounded-xl font-bold text-sm ${token.isNative
                                ? "bg-primary/20 text-primary"
                                : "bg-muted text-muted-foreground"
                              }`}
                          >
                            {token.symbol?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-foreground font-medium text-sm">
                              {token.symbol}
                            </span>
                            {token.network && (
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${getNetworkBadgeStyle(
                                  token.network
                                )}`}
                              >
                                {getNetworkBadgeText(token.network)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {token.name}
                          </p>
                        </div>

                        <div className="text-right shrink-0 w-28">
                          <p className="text-foreground text-sm font-medium truncate">
                            {showBalances
                              ? formatBalance(token.balance, token.symbol)
                              : "••••••"}
                          </p>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-muted-foreground">
                              {showBalances
                                ? formatCurrency(token.balanceUSD)
                                : "••••"}
                            </span>
                            {(token.change24hPercent ?? 0) !== 0 && (
                              <>
                                {(token.change24h ?? 0) >= 0 ? (
                                  <TrendingUp className="h-3 w-3 text-green-500" />
                                ) : (
                                  <TrendingDown className="h-3 w-3 text-[#f9cd4f]" />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Hide button - appears on hover for Solana SPL tokens */}
                      {canHide && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            hideToken(token.address);
                          }}
                          className="absolute right-1 top-1 p-1 rounded-md bg-card/90 border border-border opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:border-destructive/30"
                          title="Hide token"
                        >
                          <EyeOff className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                    );
                  }
                )}
              </div>
            )}

            {/* Hidden tokens section */}
            {normalizedHiddenTokens.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowHiddenSection(!showHiddenSection)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                >
                  {showHiddenSection ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  {showHiddenSection ? "Hide" : "Show"} hidden tokens ({normalizedHiddenTokens.length})
                </button>

                {showHiddenSection && (
                  <div className="mt-2 space-y-2 opacity-60">
                    {normalizedHiddenTokens.map((token, index) => (
                        <div
                          key={`hidden-${token.symbol}-${token.address}-${index}`}
                          className="p-3 rounded-xl bg-card/50 border border-border/50 flex items-center gap-3"
                        >
                          <Avatar className="h-8 w-8 rounded-lg">
                            {token.logoURI && (
                              <AvatarImage src={token.logoURI} alt={token.symbol} className="rounded-lg object-cover" />
                            )}
                            <AvatarFallback className="rounded-lg bg-muted text-muted-foreground font-bold text-xs">
                              {token.symbol?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-foreground/70 font-medium text-sm">
                                {token.symbol}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground/70">
                              {showBalances ? formatCurrency(token.balanceUSD) : "••••"}
                            </p>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => unhideToken(token.address)}
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Unhide
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-border">
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSendModal({ isOpen: true, token: null, network: "" });
                  setIsOpen(false);
                }}
                className="flex flex-col items-center gap-1.5 h-auto py-3 border-border hover:border-primary/50 hover:bg-primary/5"
              >
                <Send className="h-4 w-4 text-primary" />
                <span className="text-xs text-foreground">Send</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="flex flex-col items-center gap-1.5 h-auto py-3 border-border hover:border-primary/50 hover:bg-primary/5"
              >
                <ArrowUpRight className="h-4 w-4 text-primary" />
                <span className="text-xs text-foreground">Swap</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  setIsBuyOpen(true);
                }}
                className="flex flex-col items-center gap-1.5 h-auto py-3 border-border hover:border-primary/50 hover:bg-primary/5"
              >
                <CreditCard className="h-4 w-4 text-primary" />
                <span className="text-xs text-foreground">Buy</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

          {/* Overlay */}
          {isOpen && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setIsOpen(false)}
            />
          )}
        </>
      ) : (
        <div className="w-full h-full" />
      )}

      {/* Modals - ALWAYS rendered to preserve state across isConnected flips */}
      <SendModal
        isOpen={sendModal.isOpen}
        onClose={closeSendModal}
        token={sendModal.token}
        network={sendModal.network}
      />

      {/* Buy with Credit Card Modal */}
      <Dialog open={isBuyOpen} onOpenChange={setIsBuyOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              Buy {toToken?.symbol || "Crypto"} with Card
            </DialogTitle>
          </DialogHeader>
          <NewCreditCardForm />
        </DialogContent>
      </Dialog>
    </>
  );
}
